import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SETTINGS } from "@/constants/testIds";
import ImageUpload from "@/components/ImageUpload";
import { CheckCircle2, ExternalLink, RefreshCw } from "lucide-react";

export default function Settings() {
  const [venue, setVenue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/venues/me");
      setVenue(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setVenue((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await http.patch("/venues/me", {
        name: venue.name,
        logo_url: venue.logo_url,
        brand_color: venue.brand_color,
        description: venue.description,
        contact_email: venue.contact_email || null,
        website: venue.website,
        custom_domain: venue.custom_domain,
      });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const startStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await http.post("/stripe/connect/onboarding", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Stripe error");
      setConnecting(false);
    }
  };

  const refreshStripe = async () => {
    try {
      const { data } = await http.post("/stripe/connect/refresh-status");
      toast.success(data.stripe_onboarded ? "Stripe connected" : "Not yet fully onboarded");
      load();
    } catch (e) {
      toast.error("Could not refresh Stripe status");
    }
  };

  if (!venue) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Branding & settings</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">Make it yours</h1>

      <div className="mt-8 space-y-6">
        {/* Stripe Connect */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-display font-bold text-lg">Stripe payouts</h2>
              <p className="text-sm text-zinc-500 mt-1">Connect Stripe to receive ticket revenue directly.</p>
              <p className="text-xs text-zinc-500 mt-1">
                While Connect is being activated for the platform, tickets are processed via the Affra account and settled to you weekly. Direct payouts activate the moment you connect.
              </p>
              <div className="mt-3">
                {venue.stripe_onboarded ? (
                  <Badge className="bg-emerald-600"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Connected</Badge>
                ) : venue.stripe_account_id ? (
                  <Badge variant="outline" className="border-amber-300 text-amber-700">Onboarding in progress</Badge>
                ) : (
                  <Badge variant="outline">Not connected</Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {venue.stripe_account_id && (
                <Button variant="outline" className="rounded-full" onClick={refreshStripe} data-testid={SETTINGS.stripeRefreshBtn}>
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh status
                </Button>
              )}
              <Button onClick={startStripe} disabled={connecting} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid={SETTINGS.stripeConnectBtn}>
                {venue.stripe_account_id ? "Continue Stripe setup" : "Connect Stripe"}
                <ExternalLink className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Branding */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6 space-y-5">
          <h2 className="font-display font-bold text-lg">Brand</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Venue name</Label>
              <Input value={venue.name || ""} onChange={(e) => update("name", e.target.value)} data-testid={SETTINGS.venueName} className="mt-2 h-11" />
            </div>
            <div>
              <Label>Brand colour</Label>
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={venue.brand_color || "#4338CA"} onChange={(e) => update("brand_color", e.target.value)} className="w-11 h-11 rounded-md border border-zinc-200 cursor-pointer bg-white p-0" />
                <Input value={venue.brand_color || ""} onChange={(e) => update("brand_color", e.target.value)} data-testid={SETTINGS.brandColor} className="h-11 flex-1" />
              </div>
            </div>
          </div>
          <div>
            <Label>Logo</Label>
            <ImageUpload value={venue.logo_url} onChange={(v) => update("logo_url", v || "")} testId={SETTINGS.logoUrl} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={venue.description || ""} onChange={(e) => update("description", e.target.value)} data-testid={SETTINGS.description} className="mt-2" rows={3} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={venue.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} data-testid={SETTINGS.contactEmail} className="mt-2 h-11" />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={venue.website || ""} onChange={(e) => update("website", e.target.value)} data-testid={SETTINGS.website} className="mt-2 h-11" placeholder="https://yourvenue.com" />
            </div>
          </div>
        </section>

        {/* Domain */}
        <section className="rounded-xl bg-white border border-zinc-200 p-6 space-y-4">
          <h2 className="font-display font-bold text-lg">Domain</h2>
          <div>
            <Label>Your public link</Label>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <code className="px-3 py-2 bg-zinc-100 rounded-md text-sm font-mono">
                {window.location.origin}/v/{venue.slug}
              </code>
              <a href={`/v/${venue.slug}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="rounded-full">Open <ExternalLink className="w-3.5 h-3.5 ml-1" /></Button>
              </a>
            </div>
          </div>
          <div>
            <Label>Custom domain (coming soon)</Label>
            <Input value={venue.custom_domain || ""} onChange={(e) => update("custom_domain", e.target.value)} data-testid={SETTINGS.customDomain} className="mt-2 h-11" placeholder="yourvenue.affra.io" />
            <div className="mt-1 text-xs text-zinc-500">
              Add your desired subdomain here. Our team will complete DNS + SSL setup for you.
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-full bg-zinc-900 hover:bg-zinc-800 h-11 px-6" data-testid={SETTINGS.save}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
